import PageMeta from "components/PageMeta";
import WaitingDots from "components/WaitingDots";
import { useAdminConsoleVisibility } from "components/AdminConsole/adminConsoleVisibility";
import { Account } from "models/Account";
import { Tenant } from "models/Tenant";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import * as api from "services/Api";
import AccountDetailsCards from "./components/AccountDetailsCards";
import AccountExpensesCard from "./components/AccountExpensesCard";
import AccountInvoicesCard from "./components/AccountInvoicesCard";
import AccountPersonsCard from "./components/AccountPersonsCard";
import AccountProjectsCard from "./components/AccountProjectsCard";
import AccountAttachmentsCard from "./components/AccountAttachmentsCard";
import AccountQuotesCard from "./components/AccountQuotesCard";
import AccountRecentEmailsCard from "./components/AccountRecentEmailsCard";
import AccountRecurringsCard from "./components/AccountRecurringsCard";
import AccountAssetsCard from "./components/AccountAssetsCard";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

const AccountDetail: React.FC = () => {
    const { t } = useOfficeTranslation();
    const { id = "" } = useParams();
    const navigate = useNavigate();
    const adminConsoleOpen = useAdminConsoleVisibility();
    const [account, setAccount] = useState<Account | null>(null);
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [saving, setSaving] = useState(false);
    const [mergeOpen, setMergeOpen] = useState(false);
    const [mergeAccounts, setMergeAccounts] = useState<Account[]>([]);
    const [mergeTargetId, setMergeTargetId] = useState("");
    const [merging, setMerging] = useState(false);
    const [message, setMessage] = useState<{ color: string; text: string } | null>(null);
    useEffect(() => { Promise.all([api.getAccount(id).then(setAccount), api.getTenants().then(setTenants)]).catch(() => setMessage({ color: "danger", text: t("accounts.accountDetail.accountCouldNotBeLoaded") })); }, [id, t]);
    if (!account) return <div className="page-content">{message ? <Alert color={message.color}>{message.text}</Alert> : <WaitingDots />}</div>;
    const save = async (updatedAccount: Account): Promise<boolean> => {
        setSaving(true);
        setMessage(null);
        try {
            const saved = await api.updateAccount(updatedAccount);
            setAccount(saved);
            setMessage({ color: "success", text: t("accounts.accountDetail.accountSaved") });
            return true;
        } catch (reason: any) {
            setMessage({ color: "danger", text: String(reason.response?.data?.error ?? reason) });
            return false;
        } finally {
            setSaving(false);
        }
    };
    const remove = () => { if (!window.confirm(t("common.deleteNameThisCannotBeUndone", { name: account.legal_name }))) return; api.deleteAccount(account.id).then(() => navigate("/accounts")); };
    const openMerge = async () => {
        setMessage(null);
        setMergeTargetId("");
        try {
            const accounts = await api.getAccounts();
            setMergeAccounts(accounts.filter(candidate => candidate.id !== account.id && candidate.tenant === account.tenant));
            setMergeOpen(true);
        } catch (reason: any) {
            setMessage({ color: "danger", text: String(reason.response?.data?.error ?? reason) });
        }
    };
    const merge = async () => {
        if (!mergeTargetId) return;
        setMerging(true);
        setMessage(null);
        try {
            const result = await api.mergeAccount(account.id, mergeTargetId);
            setMergeOpen(false);
            navigate(`/accounts/${result.account.id}`);
        } catch (reason: any) {
            setMessage({ color: "danger", text: String(reason.response?.data?.error ?? reason) });
        } finally {
            setMerging(false);
        }
    };
    return <div className="page-content">
        <PageMeta title={account.legal_name} />
        <div className="page-title-box d-flex justify-content-between align-items-center">
            <div>
                <Link to="/accounts" className="text-muted"><i className="mdi mdi-arrow-left me-1" />{t("common.accounts")}</Link>
                <h4 className="mt-2 mb-0">{account.legal_name}</h4>
            </div>
            {adminConsoleOpen &&
                <div className="d-flex gap-2">
                    <button className="btn btn-outline-primary" type="button" onClick={openMerge}>
                        <i className="mdi mdi-merge me-1" />{t("accounts.accountDetail.merge")}
                    </button>
                    <button className="btn btn-outline-danger" type="button" onClick={remove}>
                        <i className="mdi mdi-delete-outline me-1" />{t("common.delete")}
                    </button>
                </div>
            }
        </div>
        {message && <Alert color={message.color}>{message.text}</Alert>}
        <Row className="g-4 align-items-start">
            <Col xl={4} lg={6}>
                <AccountDetailsCards account={account} tenants={tenants} saving={saving} vatLookup={api.vatCheck} kvkLookup={api.kvkCheck} onSave={save} />
                {(account.roles ?? []).includes("customer") && <AccountAssetsCard accountId={account.id} tenantId={account.tenant} />}
            </Col>
            <Col xl={4} lg={6}>
                <AccountProjectsCard accountId={account.id} />
                <AccountInvoicesCard accountId={account.id} />
                <AccountQuotesCard accountId={account.id} />
                <AccountRecurringsCard accountId={account.id} />
                <AccountExpensesCard accountId={account.id} />
            </Col>
            <Col xl={4} lg={6}>
                <AccountPersonsCard account={account} onChange={setAccount} />
                <AccountAttachmentsCard accountId={account.id} tenantId={account.tenant} />
                <AccountRecentEmailsCard accountId={account.id} />
            </Col>
        </Row>
        <Modal isOpen={mergeOpen} toggle={() => !merging && setMergeOpen(false)}>
            <ModalHeader toggle={() => !merging && setMergeOpen(false)}>{t("accounts.accountDetail.mergeRelations")}</ModalHeader>
            <ModalBody>
                <div className="alert alert-warning" role="alert">
                    {t("accounts.accountDetail.mergeWarning", { source: account.legal_name })}
                </div>
                <label className="form-label" htmlFor="merge-account-target">{t("accounts.accountDetail.mergeInto")}</label>
                <select
                    className="form-select"
                    id="merge-account-target"
                    value={mergeTargetId}
                    onChange={event => setMergeTargetId(event.target.value)}
                    disabled={merging}
                >
                    <option value="">{t("accounts.accountDetail.selectMergeTarget")}</option>
                    {mergeAccounts.map(candidate =>
                        <option value={candidate.id} key={candidate.id}>{candidate.legal_name}</option>
                    )}
                </select>
                {!mergeAccounts.length && <p className="text-muted mt-3 mb-0">{t("accounts.accountDetail.noMergeTargets")}</p>}
            </ModalBody>
            <ModalFooter>
                <button className="btn btn-light" type="button" onClick={() => setMergeOpen(false)} disabled={merging}>{t("common.cancel")}</button>
                <button className="btn btn-danger" type="button" onClick={merge} disabled={!mergeTargetId || merging}>
                    {merging ? t("accounts.accountDetail.merging") : t("accounts.accountDetail.confirmMerge")}
                </button>
            </ModalFooter>
        </Modal>
    </div>;
};
export default AccountDetail;
